import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Add as AddIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import {
  Box, Button,
  Grid,
  Stack,
  Paper,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Collapse,
  IconButton
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';

import { errorToast, successToast } from '@/common/toast';
import { Container, ErrorMessage } from '@/components/common';
import { useCreateDiagnosticProblem, useDiagnosticMainCategories, useDiagnosticSubCategories } from '@/hooks/diagnostic.hook';
import {
  MonacoEditorStyles,
  TrainingBasicInfoForm,
  Step2LineAnswersInput,
} from './components';
import { getMonacoLanguage } from './utils/getMonacoLanguage';
import {
  RESIZABLE_THREE_COLUMNS,
  ResizeColumnHandle,
  useResizableThreeColumns,
} from './utils/resizableThreeColumns';
import { MAX_STEP_FILES } from '@/constants/trainings';
import { areStep2BlankLineAnswersFilled } from './utils/step2LineAnswersValidation';

/**
 * 진단 및 대응훈련 문제 등록 페이지 컴포넌트
 * 
 * 새로운 문제를 출제할 수 있는 페이지입니다.
 */
const TrainingAddPage = () => {
  // authAtom 사용자 정보 조회
  const { userId } = JSON.parse(sessionStorage.getItem('authAtom')) || { userId: 'admin' };
  const navigate = useNavigate();

  const createTrainingMutation = useCreateDiagnosticProblem();
  // 언어 선택 상태 (기본값: LANGUAGE_OPTIONS의 첫 번째 값)
  const [languageId, setLanguageId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);

  // 상위 탭 선택 상태 (1단계/2단계)
  const [activeMainTab, setActiveMainTab] = useState(0);

  // Monaco Editor 인스턴스와 decorations 저장 (각 파일별로 관리)
  const editorRefs = useRef({}); // { step1Files[0]: editor, step2Files[0]: editor }
  const decorationsRefs = useRef({}); // { step1Files[0]: [decorationIds], step2Files[0]: [decorationIds] }

  // 폼 관리
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    getValues,
    setValue,
  } = useForm({
    defaultValues: {
      categoryId: '',
      subCategoryId: '',
      difficulty: '',
      languageId: languageId,
      hint1: '',
      hint2: '',
      step1Files: [], // {fileName, content, lineNum}
      step2Files: [], // {fileName, content, keyword}
    },
  });

  // 1단계 파일+정답 필드 배열
  const { fields: step1Fields, append: appendStep1, remove: removeStep1 } = useFieldArray({
    control,
    name: 'step1Files',
  });

  // 2단계 파일+정답 필드 배열
  const { fields: step2Fields, append: appendStep2, remove: removeStep2 } = useFieldArray({
    control,
    name: 'step2Files',
  });

  // 카테고리 및 서브카테고리 조회
  const { data: categoriesData } = useDiagnosticMainCategories();
  const { data: subCategoriesData } = useDiagnosticSubCategories(selectedCategoryId);

  const categories = categoriesData || [];
  const subCategories = subCategoriesData || [];

  // 카테고리 선택 핸들러
  const handleCategoryChange = useCallback((categoryId) => {
    setSelectedCategoryId(categoryId);
    setSelectedSubCategoryId(null);
    // 서브카테고리 폼 값도 초기화
    setValue('subCategoryId', '');
  }, [setValue]);

  // 서브카테고리 선택 핸들러
  const handleSubCategoryChange = useCallback((subCategoryId) => {
    setSelectedSubCategoryId(subCategoryId);
  }, []);

  // 언어 선택 변경 핸들러
  const handleLanguageChange = useCallback((langId) => {
    setLanguageId(langId);
  }, []);

  // 폼의 languageId 값 감시하여 상태 동기화
  const formLanguageId = watch('languageId');
  useEffect(() => {
    if (formLanguageId && formLanguageId !== languageId) {
      setLanguageId(formLanguageId);
    }
  }, [formLanguageId, languageId]);


  // 현재 단계의 파일 목록
  const currentFiles = useMemo(() => {
    return activeMainTab === 0 ? step1Fields : step2Fields;
  }, [activeMainTab, step1Fields, step2Fields]);

  const canAddMoreFiles = useMemo(
    () => currentFiles.length < MAX_STEP_FILES,
    [currentFiles.length]
  );

  // 선택된 파일 인덱스
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);

  // 현재 선택된 파일
  const currentFile = useMemo(() => {
    if (currentFiles.length === 0) return null;
    return currentFiles[selectedFileIndex] || currentFiles[0];
  }, [currentFiles, selectedFileIndex]);

  // 힌트 표시 여부
  const [showHint, setShowHint] = useState(true);

  const {
    containerRef: mainAreaRef,
    leftWidth: explorerWidth,
    rightWidth: hintWidth,
    startResizeLeft: startResizeExplorer,
    startResizeRight: startResizeHint,
    handleWidthPx,
    minLeft: minExplorerWidth,
    minRight: minHintWidth,
    minCenter: minCenterWidth,
  } = useResizableThreeColumns();

  // 파일 선택 핸들러
  const handleFileSelect = useCallback((index) => {
    setSelectedFileIndex(index);
  }, []);

  // 라인 파싱 헬퍼 (문자열 "1,2,3" <-> 배열 [1,2,3])
  const getLineNumbers = useCallback((val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.trim()) {
      return val.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
    }
    return [];
  }, []);

  // Decoration (하이라이트) 업데이트 로직 - 모든 라인에 glyph 표시하여 클릭 가능하게
  const updateDecorations = useCallback((editor, lines, editorKey) => {
    if (!editor || !editor.getModel()) return;

    const decorationIds = decorationsRefs.current[editorKey] || [];
    const lineNumbers = getLineNumbers(lines);
    const selectedLinesSet = new Set(lineNumbers);
    const totalLines = editor.getModel().getLineCount();
    const newDecos = [];

    for (let lineNumber = 1; lineNumber <= totalLines; lineNumber++) {
      const isSelected = selectedLinesSet.has(lineNumber);
      newDecos.push({
        range: { startLineNumber: lineNumber, startColumn: 1, endLineNumber: lineNumber, endColumn: 1 },
        options: {
          isWholeLine: true,
          className: isSelected ? 'selected-line-highlight' : '',
          glyphMarginClassName: isSelected ? 'selected-line-glyph' : 'unselected-line-glyph',
        }
      });
    }

    try {
      const newIds = editor.deltaDecorations(decorationIds, newDecos);
      decorationsRefs.current[editorKey] = newIds;
    } catch (e) { console.warn(e); }
  }, [getLineNumbers]);

  // 파일 삭제 핸들러
  const handleRemoveFile = useCallback((index) => {
    if (activeMainTab === 0) {
      removeStep1(index);
      if (selectedFileIndex >= step1Fields.length - 1 && selectedFileIndex > 0) {
        setSelectedFileIndex(selectedFileIndex - 1);
      } else if (selectedFileIndex >= step1Fields.length - 1) {
        setSelectedFileIndex(0);
      }
    } else {
      removeStep2(index);
      if (selectedFileIndex >= step2Fields.length - 1 && selectedFileIndex > 0) {
        setSelectedFileIndex(selectedFileIndex - 1);
      } else if (selectedFileIndex >= step2Fields.length - 1) {
        setSelectedFileIndex(0);
      }
    }
  }, [activeMainTab, step1Fields.length, step2Fields.length, selectedFileIndex, removeStep1, removeStep2]);

  // 파일 추가 핸들러
  const handleAddFile = useCallback(() => {
    if (activeMainTab === 0) {
      if (step1Fields.length >= MAX_STEP_FILES) {
        errorToast(`파일은 단계당 최대 ${MAX_STEP_FILES}개까지 추가할 수 있습니다.`);
        return;
      }
      appendStep1({ fileName: '', content: '', lines: null });
      setTimeout(() => {
        setSelectedFileIndex(step1Fields.length);
      }, 0);
    } else {
      if (step2Fields.length >= MAX_STEP_FILES) {
        errorToast(`파일은 단계당 최대 ${MAX_STEP_FILES}개까지 추가할 수 있습니다.`);
        return;
      }
      appendStep2({ fileName: '', content: '', word: '', lineAnswers: [] });
      setTimeout(() => {
        setSelectedFileIndex(step2Fields.length);
      }, 0);
    }
  }, [activeMainTab, appendStep1, appendStep2, step1Fields.length, step2Fields.length]);

  // 단계 변경 핸들러
  const handleStepChange = useCallback((event, newValue) => {
    setActiveMainTab(newValue);
    setSelectedFileIndex(0);
  }, []);

  // 파일 삭제 시 인덱스 자동 조정
  useEffect(() => {
    if (activeMainTab === 0) {
      if (selectedFileIndex >= step1Fields.length && step1Fields.length > 0) {
        setSelectedFileIndex(Math.max(0, step1Fields.length - 1));
      }
    } else {
      if (selectedFileIndex >= step2Fields.length && step2Fields.length > 0) {
        setSelectedFileIndex(Math.max(0, step2Fields.length - 1));
      }
    }
  }, [activeMainTab, step1Fields.length, step2Fields.length, selectedFileIndex]);

  // 문제 등록 핸들러
  const onSubmit = useCallback(
    async (data) => {
      try {
        const { difficulty, trainingName, step1Files, step2Files, ...restData } = data; // trainingName 제거

        if (!areStep2BlankLineAnswersFilled(step2Files)) {
          errorToast('입력하지 않은 2단계 정답이 있습니다.');
          return;
        }

        // step1Files를 files1과 answer1로 분리
        const files1 = step1Files
          .map(file => ({
            fileName: file.fileName.trim(),
            content: file.content,
            lineNum: file.lines !== null && file.lines !== '' ? file.lines : null,
        }));
          
        const files2 = step2Files
          .map(file => {
            const lineAnswersFiltered = Array.isArray(file.lineAnswers)
              ? file.lineAnswers.filter(Boolean)
              : [];
            const answers = lineAnswersFiltered.length > 0
              ? lineAnswersFiltered.join('/')
              : (file.word || null);
            return {
              fileName: file.fileName.trim(),
              content: file.content,
              // answers의 공백을 ,로 치환하여 keyword에 할당
              keyword: typeof answers === 'string' ? answers.replace(/\s+/g, ',') : answers,
            };
        });

        const requestData = {
          languageId: languageId,
          subCategoryId: parseInt(data.subCategoryId),
          level: data.difficulty,
          createdBy: userId,
          vulDesc: data.hint1,
          secDesc: data.hint2,
          vulFiles: files1,
          secFiles: files2,
        }

        await createTrainingMutation.mutateAsync(requestData);

        successToast('문제가 등록되었습니다.');
        navigate(-1);
      } catch (error) {
        errorToast('문제 등록에 실패했습니다.');
        console.error('등록 오류:', error);
      }
    },
    [createTrainingMutation, navigate, languageId]
  );

  // 뒤로가기 핸들러
  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <>
      <MonacoEditorStyles />
      <Container title='문제 출제'>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* 상단 버튼 영역 */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant='outlined'
              onClick={handleGoBack}
              startIcon={<ArrowBackIcon />}
              sx={{ minWidth: 120 }}
            >
              목록으로
            </Button>
            <Button
              variant='contained'
              onClick={handleSubmit(onSubmit)}
              startIcon={<SaveIcon />}
              disabled={!isDirty || createTrainingMutation.isPending}
              sx={{ minWidth: 100 }}
            >
              등록
            </Button>
          </Box>

          {/* 기본 정보 폼 */}
          <Paper sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
            <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>기본 정보</Typography>
            <Grid container spacing={2}>
              <TrainingBasicInfoForm
                control={control}
                errors={errors}
                categories={categories}
                subCategories={subCategories}
                selectedCategoryId={selectedCategoryId}
                onCategoryChange={handleCategoryChange}
                onSubCategoryChange={handleSubCategoryChange}
                onLanguageChange={handleLanguageChange}
              />
            </Grid>
          </Paper>

          {/* 단계 탭 */}
          <Box sx={{ mb: 2 }}>
            <Tabs value={activeMainTab} onChange={handleStepChange}>
              <Tab label="1단계" />
              <Tab label="2단계" />
            </Tabs>
          </Box>

          {/* 메인 영역: VS Code 스타일 레이아웃 (리사이즈 핸들로 열 너비 조절) */}
          <Box
            ref={mainAreaRef}
            sx={{
              display: 'flex',
              flex: 1,
              alignItems: 'stretch',
              minHeight: 0,
              width: '100%',
            }}
          >
            {/* 왼쪽: 파일 탐색기 */}
            <Paper
              sx={{
                width: explorerWidth,
                minWidth: minExplorerWidth,
                maxWidth: `${RESIZABLE_THREE_COLUMNS.maxLeftPercent}%`,
                flexShrink: 0,
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ p: 1, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant='body2' fontWeight={600}>
                  파일 탐색기
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleAddFile}
                  color="primary"
                  disabled={!canAddMoreFiles}
                  title={canAddMoreFiles ? '파일 추가' : `최대 ${MAX_STEP_FILES}개까지 추가 가능`}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              <List dense sx={{ p: 0, flex: 1, overflow: 'auto' }}>
                {currentFiles.map((field, index) => {
                  const fileName = watch(activeMainTab === 0 ? `step1Files.${index}.fileName` : `step2Files.${index}.fileName`);
                  return (
                    <ListItem key={field.id} disablePadding>
                      <ListItemButton
                        selected={index === selectedFileIndex}
                        onClick={() => handleFileSelect(index)}
                        sx={{
                          '&.Mui-selected': {
                            bgcolor: '#e3f2fd',
                          }
                        }}
                      >
                        <ListItemText
                          primary={fileName || `파일 ${index + 1}`}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            sx: {
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                            },
                          }}
                          sx={{ flex: 1, minWidth: 0 }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(index);
                          }}
                          color="error"
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
                {currentFiles.length === 0 && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant='body2' color='text.secondary'>
                      파일이 없습니다.
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddFile}
                      disabled={!canAddMoreFiles}
                      sx={{ mt: 1 }}
                    >
                      파일 추가
                    </Button>
                  </Box>
                )}
              </List>
            </Paper>

            <ResizeColumnHandle
              ariaLabel="파일 탐색기와 에디터 너비 조절"
              onMouseDown={startResizeExplorer}
              handleWidthPx={handleWidthPx}
            />

            {/* 중앙: 에디터 영역 */}
            <Box sx={{ flex: 1, minWidth: minCenterWidth, display: 'flex', flexDirection: 'column' }}>
              {currentFile ? (
                <>
                  {/* 파일 탭 바 */}
                  <Paper sx={{ p: 1, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant='body2' fontWeight={500}>
                      {watch(activeMainTab === 0 ? `step1Files.${selectedFileIndex}.fileName` : `step2Files.${selectedFileIndex}.fileName`) || `파일 ${selectedFileIndex + 1}`}
                    </Typography>
                  </Paper>

                  {/* 파일명 입력 */}
                  <Paper sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                    <Controller
                      control={control}
                      name={activeMainTab === 0 ? `step1Files.${selectedFileIndex}.fileName` : `step2Files.${selectedFileIndex}.fileName`}
                      rules={{ required: '파일명을 입력해주세요.' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="파일명"
                          size="small"
                          fullWidth
                          placeholder="예: Main.java"
                          error={!!errors[activeMainTab === 0 ? 'step1Files' : 'step2Files']?.[selectedFileIndex]?.fileName}
                        />
                      )}
                    />
                  </Paper>

                  {/* Monaco Editor - 최소 높이 보장 */}
                  <Box sx={{ flex: 1, minHeight: 500, border: '1px solid #e0e0e0', borderRadius: 1, mt: 1 }}>
                    <Controller
                      control={control}
                      name={activeMainTab === 0 ? `step1Files.${selectedFileIndex}.content` : `step2Files.${selectedFileIndex}.content`}
                      rules={{ required: '파일 내용을 입력해주세요.' }}
                      render={({ field }) => {
                        const fileName = watch(activeMainTab === 0 ? `step1Files.${selectedFileIndex}.fileName` : `step2Files.${selectedFileIndex}.fileName`);
                        const editorKey = activeMainTab === 0 ? `step1Files-${selectedFileIndex}` : `step2Files-${selectedFileIndex}`;
                        const linesValue = activeMainTab === 0 ? watch(`step1Files.${selectedFileIndex}.lines`) : null;
                        
                        return (
                          <Editor
                            key={editorKey}
                            height="100%"
                            language={getMonacoLanguage(fileName)}
                            theme="vs-light"
                            defaultValue={field.value || ''}
                            onChange={(val) => {
                              field.onChange(val || '');
                              if (activeMainTab === 0) {
                                const editor = editorRefs.current[editorKey];
                                updateDecorations(editor, linesValue, editorKey);
                              }
                            }}
                            onMount={(editor, monaco) => {
                              editorRefs.current[editorKey] = editor;

                              if (activeMainTab === 0) {
                                updateDecorations(editor, linesValue, editorKey);

                                editor.onMouseDown((e) => {
                                  const isLineClick = e.target.type === monaco.editor.MouseTargetType.GUTTER_LINE_NUMBERS ||
                                    e.target.type === monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN;
                                  if (isLineClick && e.target.position?.lineNumber) {
                                    const clickedLine = e.target.position.lineNumber;
                                    const currentString = getValues(`step1Files.${selectedFileIndex}.lines`);
                                    const currentLines = getLineNumbers(currentString);

                                    let newLines;
                                    if (currentLines.includes(clickedLine)) {
                                      newLines = currentLines.filter(n => n !== clickedLine);
                                    } else {
                                      newLines = [...currentLines, clickedLine].sort((a, b) => a - b);
                                    }

                                    const newString = newLines.join(',');
                                    setValue(`step1Files.${selectedFileIndex}.lines`, newString, {
                                      shouldDirty: true,
                                      shouldValidate: true
                                    });

                                    updateDecorations(editor, newString, editorKey);
                                  }
                                });
                              }
                            }}
                            options={{
                              minimap: { enabled: true },
                              fontSize: 14,
                              scrollBeyondLastLine: false,
                              wordWrap: 'on',
                              readOnly: false,
                              glyphMargin: activeMainTab === 0,
                              selectOnLineNumbers: false,
                              lineNumbersMinChars: 3,
                            }}
                          />
                        );
                      }}
                    />
                  </Box>

                  {/* 정답 입력 영역 */}
                  <Paper sx={{ p: 2, mt: 2, border: '1px solid #e0e0e0' }}>
                    {activeMainTab === 0 ? (
                      <Controller
                        control={control}
                        name={`step1Files.${selectedFileIndex}.lines`}
                        render={({ field }) => (
                          <TextField
                            label="선택된 정답 라인 (에디터 라인 번호 클릭)"
                            size="small"
                            fullWidth
                            value={field.value || ''}
                            InputProps={{
                              readOnly: true,
                            }}
                            helperText="위 에디터에서 라인 번호를 클릭하면 자동으로 입력됩니다."
                            sx={{ bgcolor: '#f5f5f5' }}
                          />
                        )}
                      />
                    ) : (
                      <Step2LineAnswersInput
                        setValue={setValue}
                        watch={watch}
                        selectedFileIndex={selectedFileIndex}
                      />
                    )}
                  </Paper>
                </>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                    파일이 없습니다.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddFile}
                    disabled={!canAddMoreFiles}
                  >
                    파일 추가
                  </Button>
                </Paper>
              )}
            </Box>

            <ResizeColumnHandle
              ariaLabel="에디터와 힌트 패널 너비 조절"
              onMouseDown={startResizeHint}
              handleWidthPx={handleWidthPx}
            />

            {/* 오른쪽: 힌트 패널 */}
            <Paper
              sx={{
                width: hintWidth,
                minWidth: minHintWidth,
                maxWidth: `${RESIZABLE_THREE_COLUMNS.maxRightPercent}%`,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
              }}
            >
              {/* 힌트 섹션 */}
              <Box>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#f5f5f5',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowHint(!showHint)}
                >
                  <Typography variant='body2' fontWeight={600}>
                    힌트
                  </Typography>
                  {showHint ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
                </Box>
                <Collapse in={showHint}>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: activeMainTab === 0 ? 'block' : 'none' }}>
                      <Controller
                        control={control}
                        name='hint1'
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            multiline
                            rows={6}
                            label='1단계 힌트'
                            variant='outlined'
                            placeholder='1단계 문제 해결을 위한 힌트를 입력해주세요.'
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ display: activeMainTab === 1 ? 'block' : 'none' }}>
                      <Controller
                        control={control}
                        name='hint2'
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            multiline
                            rows={6}
                            label='2단계 힌트'
                            variant='outlined'
                            placeholder='2단계 문제 해결을 위한 힌트를 입력해주세요.'
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Monaco Editor 라인 하이라이트 스타일 (unselected-line-glyph는 MonacoEditorStyles에서 제공) */}
        <style>{`
          .monaco-editor .selected-line-highlight {
            background-color: rgba(0, 122, 204, 0.2) !important;
          }
          .monaco-editor .selected-line-glyph {
            background-color: transparent !important;
            width: 18px !important;
            background-image: url("data:image/svg+xml;utf8,<svg width='16' height='16' xmlns='http://www.w3.org/2000/svg'><rect x='2' y='2' width='12' height='12' rx='2' style='fill:%23007acc;stroke:%23007acc;stroke-width:1'/><polyline points='4,8 7,11 12,5' style='fill:none;stroke:%23ffffff;stroke-width:2;stroke-linecap:round;stroke-linejoin:round'/></svg>");
            background-repeat: no-repeat;
            background-position: center;
            cursor: pointer !important;
          }
          .monaco-editor .unselected-line-glyph {
            background-color: transparent !important;
            width: 18px !important;
            background-image: url("data:image/svg+xml;utf8,<svg width='16' height='16' xmlns='http://www.w3.org/2000/svg'><rect x='2' y='2' width='12' height='12' rx='2' style='fill:none;stroke:%23666666;stroke-width:1.5'/></svg>");
            background-repeat: no-repeat;
            background-position: center;
            cursor: pointer !important;
          }
          .monaco-editor .unselected-line-glyph:hover {
            background-image: url("data:image/svg+xml;utf8,<svg width='16' height='16' xmlns='http://www.w3.org/2000/svg'><rect x='2' y='2' width='12' height='12' rx='2' style='fill:none;stroke:%23999999;stroke-width:1.5'/></svg>");
          }
        `}</style>
      </Container>
    </>
  );
};

export default TrainingAddPage;
